import React from "react";
import { Box, Flex, Text, Textarea, VStack } from "@chakra-ui/react";
import { getTooltip } from "utils/getMessages";

import { ScheduledJobProto } from "build-ts/schedule";
import CronBuilder from "components/analyst/CronBuilder";
import WorkflowSectionHeading from "modules/analyst/components/WorkflowSectionHeading";
import { MarkdownRenderer } from "modules/common/MarkdownRenderer";

// Fixed heights shared between view-mode boxes and edit-mode textareas so the
// layout never shifts when toggling edit mode.
const DESCRIPTION_H = "130px";
const INSTRUCTIONS_H = "290px";

const sharedBoxStyle = {
  borderWidth: "1px",
  borderRadius: "md",
  px: 4,
  py: 3,
  overflowY: "auto" as const,
  overflowX: "auto" as const,
};

const sharedTextareaStyle = {
  fontSize: "sm",
  resize: "vertical" as const,
  borderColor: "purple.300",
  _focus: { borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" },
  bg: "white",
};

export interface WorkflowDefinitionProps {
  trigger: string;
  reportingInstructions: string;
  cronExpression: string;
  setCronExpression: React.Dispatch<React.SetStateAction<string>>;
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
  selectedJobDetails: ScheduledJobProto | undefined;
  onViewExecutionLog?: () => void;
  /** Called after a successful schedule save so the parent can run post-save side effects. */
  onWorkflowSaved?: (savedJob?: ScheduledJobProto) => void;
  // When true, description and instructions fields are rendered as editable textareas.
  isEditMode?: boolean;
  triggerDraft?: string;
  setTriggerDraft?: (v: string) => void;
  reportingDraft?: string;
  setReportingDraft?: (v: string) => void;
}

const WorkflowDefinition: React.FC<WorkflowDefinitionProps> = ({
  trigger,
  reportingInstructions,
  cronExpression,
  setCronExpression,
  isActive,
  setIsActive,
  selectedJobDetails,
  onViewExecutionLog,
  onWorkflowSaved,
  isEditMode = false,
  triggerDraft = "",
  setTriggerDraft,
  reportingDraft = "",
  setReportingDraft,
}) => {
  return (
    <Flex direction="column" flex="1" minH={0} h="100%" overflow="hidden" px={2}>
      <Box maxW="900px" mx="auto" w="100%" flex="1" minH={0} overflowY="auto" px={4} py={5}>
        <VStack spacing={6} align="stretch">
          {/* Description (Trigger) */}
          <Box>
            <WorkflowSectionHeading
              compact
              label="Description"
              tooltip={getTooltip("workflow_detail.description.tooltip")}
            />
            {isEditMode ? (
              <Textarea
                {...sharedTextareaStyle}
                h={DESCRIPTION_H}
                value={triggerDraft}
                onChange={e => setTriggerDraft?.(e.target.value)}
              />
            ) : (
              <Box {...sharedBoxStyle} h={DESCRIPTION_H} borderColor="gray.200" bg="gray.50">
                {trigger.trim() ? (
                  <MarkdownRenderer content={trigger} />
                ) : (
                  <Text fontSize="sm" color="gray.400" mb={0}>
                    No description set.
                  </Text>
                )}
              </Box>
            )}
          </Box>

          {/* Instructions */}
          <Box>
            <WorkflowSectionHeading
              compact
              label="Instructions"
              tooltip={getTooltip("workflow_detail.instructions.tooltip")}
            />
            {isEditMode ? (
              <Textarea
                {...sharedTextareaStyle}
                h={INSTRUCTIONS_H}
                value={reportingDraft}
                onChange={e => setReportingDraft?.(e.target.value)}
              />
            ) : (
              <Box {...sharedBoxStyle} h={INSTRUCTIONS_H} borderColor="gray.200" bg="gray.50">
                {reportingInstructions.trim() ? (
                  <MarkdownRenderer content={reportingInstructions} />
                ) : (
                  <Text fontSize="sm" color="gray.400" mb={0}>
                    No instructions set.
                  </Text>
                )}
              </Box>
            )}
          </Box>

          {/* Schedule — always editable via CronBuilder */}
          <Box>
            <WorkflowSectionHeading label="Schedule" tooltip={getTooltip("workflow_detail.schedule.tooltip")} compact />
            <CronBuilder
              cronExpression={cronExpression}
              setCronExpression={setCronExpression}
              isActive={isActive}
              setIsActive={setIsActive}
              jobDetails={selectedJobDetails}
              onViewExecutionLog={onViewExecutionLog}
              onSaveComplete={onWorkflowSaved}
            />
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
};

export default WorkflowDefinition;
